import axios from "axios";

import { isNativePlatform } from "../capacitor/platform";
import { getServerUrl } from "../capacitor/server-url";
import { getSessionToken } from "../capacitor/session-token";
import { peekClientFingerprint } from "./fingerprint";

// Transport layer only. This module is a dependency-free leaf: it knows how to
// talk to the backend (base URL, credentials, native bearer header) but nothing
// about routing or app state. Response policy (401 redirects, session teardown,
// recovery-mode handling) lives in responseInterceptors.ts, which is wired up by
// the entrypoints so this module never needs to import the router or stores.
//
// On web, the session lives in an HttpOnly cookie set by the server, so axios
// must send credentials. On native, the session is a bearer token in
// hardware-backed secure storage, so cookies are unused (and would be flaky
// across user-configured server URLs anyway).
const instance = axios.create({
  withCredentials: !isNativePlatform(),
  baseURL: "/api",
  headers: {
    "content-type": "application/json",
  },
});

// Attach the client fingerprint (ThumbmarkJS, computed once and cached) on every
// request, all platforms. Recorded server-side beside IP + User-Agent. This must
// never delay a request: we only read an already-computed value (peekClientFingerprint
// is synchronous and never awaits), so requests that fire before the ~cold-load
// computation settles simply go out without the headers rather than stalling on the
// critical path. Later requests carry the fingerprint once it is ready.
instance.interceptors.request.use((config) => {
  const fp = peekClientFingerprint();
  if (fp?.hash) {
    config.headers.set("X-Client-Fingerprint", fp.hash);
  }
  if (fp?.data) {
    config.headers.set("X-Client-Fingerprint-Data", fp.data);
  }
  return config;
});

if (isNativePlatform()) {
  instance.interceptors.request.use(async (config) => {
    const serverUrl = await getServerUrl();
    if (serverUrl) {
      config.baseURL = `${serverUrl}/api`;
    }
    // Identify ourselves so login/token-exchange responses skip Set-Cookie
    // and return the session token in the JSON body for storage in Keystore
    // / Keychain.
    config.headers.set("X-Client-Type", "native");
    const token = await getSessionToken();
    if (token) {
      config.headers.set("Authorization", `Bearer ${token}`);
    }
    return config;
  });
}

export default instance;
